import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isTransparent = isHomePage && !scrolled;
  const textClass = isTransparent
    ? 'text-white/90 hover:text-white'
    : 'text-foreground-700 hover:text-foreground-950';
  const bgClass = isTransparent
    ? 'bg-transparent'
    : 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70';
  const logoTextClass = isTransparent ? 'text-white' : 'text-foreground-950';
  const logoAccentClass = 'text-primary-400';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${bgClass}`}>
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <span className={`text-lg md:text-xl font-heading font-bold tracking-tight whitespace-nowrap ${logoTextClass}`}>
              Panda<span className={isTransparent ? 'text-primary-300' : 'text-primary-500'}>Buy</span>.asia
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${textClass} ${isTransparent ? 'hover:bg-white/10' : 'hover:bg-background-100'}`}
            >
              <i className="ri-home-line text-sm"></i>
              Home
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${textClass} ${isTransparent ? 'hover:bg-white/10' : 'hover:bg-background-100'}`}
              >
                <span>Categories</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className={`absolute top-full left-0 mt-2 w-72 border rounded-xl py-2 max-h-[420px] overflow-y-auto ${isTransparent ? 'bg-background-50 border-background-200/40' : 'bg-background-50 border-background-200/80'}`}>
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap"
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 flex-shrink-0">
                        <i className={`${cat.icon} text-base`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${textClass} ${isTransparent ? 'hover:bg-white/10' : 'hover:bg-background-100'}`}
            >
              Pandabuy Sheet
            </a>

            <Link
              to="/article/shopping-guide"
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${textClass} ${isTransparent ? 'hover:bg-white/10' : 'hover:bg-background-100'}`}
            >
              <i className="ri-book-open-line text-sm"></i>
              Shopping Guide
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${isTransparent ? 'text-white hover:bg-white/10' : 'text-foreground-700 hover:bg-background-100'}`}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors"
            >
              <span className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 flex-shrink-0">
                <i className="ri-home-line text-sm"></i>
              </span>
              Home
            </Link>

            <div>
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors"
              >
                <span className="flex items-center gap-3">
                  <span className="w-7 h-7 flex items-center justify-center rounded-md bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-apps-line text-sm"></i>
                  </span>
                  Categories
                </span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {mobileCatOpen && (
                <div className="ml-3 mt-1 space-y-0.5 border-l-2 border-background-200/50 pl-3">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2 text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors"
                    >
                      <span className="w-6 h-6 flex items-center justify-center rounded-md bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className={`${cat.icon} text-xs`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/article/shopping-guide"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors"
            >
              <span className="w-7 h-7 flex items-center justify-center rounded-md bg-accent-100 text-accent-600 flex-shrink-0">
                <i className="ri-book-open-line text-sm"></i>
              </span>
              Shopping Guide
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors"
            >
              <span className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-600 flex-shrink-0">
                <i className="ri-external-link-line text-sm"></i>
              </span>
              Pandabuy Sheet
            </a>

            <div className="pt-2 space-y-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center w-full px-4 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-shopping-bag-3-line mr-2"></i>
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}